const BN = require("bignumber.js");
const { fromUnixTime, getUnixTime, subMinutes } = require("date-fns");
const { toBN, getNetwork } = require("../lib/utils");
const { getTokenPrice, getPair, getMassBlocks } = require("./graph");
const { tokenBlockPriceQuery, pairSpotQuery } = require("./graph/queries");

const FREQ_TO_DELTA_TIMESTAMP = {
  minutely: 60,
  hourly: 3600,
  daily: 86400
};

/**
 * @name PnL
 * @brief Tracks the PnL of a position given by a lot table (i.e. a set of trades or fills)
 * for a unique underlying.
 *
 * @name Fill
 * @brief Describes a trade with a signed quantity and execution price of a given underlying
 * @param timestamp is missing and should be added.
 *
 * @name updateOnFill
 * @brief Call this method every time there is a new fill.
 * @param fill a Fill instance with a signed quantity and execution price.
 *
 * @name updateOnPrice
 * @brief Call this method every time there is a market price movement.
 * @param price the most up-to-date price of an instrument.
 *
 * @name updateOnDividend
 * @brief Call this method every time there is a dividend payout.
 * @param dividend the received dividend.
 *
 * @name compute
 * @brief Performs the PnL computation up to current time considering emitted events from
 * `fromBlock` to `toBlock`.
 * @param fromBlock starting point from where Liquidity Added/Removed events are considered.
 * @param toBlock ending point from where Liquidity Added/Removed events are considered.
 * @param chunkLimit some networks have a block query limit so we perform multiple queries
 * with a `chunkLimit` size to lookup to any block domain.
 * @param freq if provided, the PnL marks to market from the first LiquidityAdded event to
 * the current time with a `freq` frequency.
 *
 */

module.exports = async (position) => {

  const contract = position.position;
  const network = await getNetwork(position.provider);

  let quantity = BN(0);
  let cost = BN(0);
  let costOfInvestment = BN(0);
  let marketValue = BN(0);
  // let averagePrice = BN(0);
  let realizedPnL = BN(0);
  let unrealizedPnL = BN(0);
  let PnL = BN(0);
  let ROI = BN(0);
  let annualizedReturn = BN(0);

  const t = [];
  const blockNumber_t = [];
  const event_t = [];
  const quantity_t = [];
  const costOfInvestment_t = [];
  const realizedPnL_t = [];
  const unrealizedPnL_t = [];
  const PnL_t = [];
  const ROI_t = [];
  const annualizedReturn_t = [];

  const fills = [];

  const isShort = () => quantity.lt(0);
  const isLong = () => quantity.gt(0);
  const isNeutral = () => quantity.eq(0);

  const getLastQuantity = () => quantity;
  const getLastCostOfInvestment = () => costOfInvestment;
  const getLastRealizedPnL = () => realizedPnL;
  const getLastUnrealizedPnL = () => unrealizedPnL;
  const getLastPnL = () => PnL;
  const getLastROI = () => ROI;
  const getLastAnnualizedReturn = () => annualizedReturn;
  const getLastMarketValue = () => marketValue;

  const Fill = (timestamp, quantity, price) => {
    const isBuy = BN(quantity).gt(0);
    const isSell = BN(quantity).lt(0);
    const side = isBuy ? 1 : isSell ? -1 : 0;

    return {
      isBuy,
      isSell,
      timestamp,
      quantity,
      price,
      side
    };
  };

  const getClosingQuantity = (fill) => {
    if ((fill.isBuy && isShort()) || (fill.isSell && isLong())) {
      return BN.min(quantity.abs(), fill.quantity.abs()).times(fill.side);
    }
    return BN(0);
  };

  const getOpeningQuantity = (fill, closingQuantity) => {
    if ((fill.isBuy && isLong()) || (fill.isSell && isShort())) {
      return fill.quantity;
    }
    return fill.quantity.minus(closingQuantity || getClosingQuantity(fill));
  };

  const updateOnFill = (fill, event) => {
    const closingQuantity = getClosingQuantity(fill);
    const openingQuantity = getOpeningQuantity(fill, closingQuantity);
    const deltaCost = openingQuantity.times(fill.price);
    let newCost = cost.plus(deltaCost);
    if (!quantity.eq(0)) {
      newCost = newCost.plus(closingQuantity.times(cost).div(quantity));
      realizedPnL = realizedPnL.plus(
        closingQuantity.times(cost.div(quantity).minus(fill.price))
      );
    }
    costOfInvestment = costOfInvestment.plus(deltaCost);
    quantity = quantity.plus(fill.quantity);
    cost = newCost;
    commonUpdate(fill.timestamp, fill.price);
    recordHistory(fill.timestamp, event);
    fills.push(fill);
  };

  const commonUpdate = (timestamp, price) => {
    // averagePrice = quantity.eq(0) ? BN(0) : cost.div(quantity);
    marketValue = quantity.times(price);
    unrealizedPnL = marketValue.minus(cost);
    PnL = realizedPnL.plus(unrealizedPnL);
    ROI = costOfInvestment.eq(0) ? BN(0) : PnL.div(costOfInvestment);
    annualizedReturn = (t.length == 0) ? BN(0) : toAnnualReturn(ROI, t[0], timestamp);
  };

  const updateOnPrice = (timestamp, marketPrice, event) => {
    commonUpdate(timestamp, marketPrice);
    recordHistory(timestamp, event);
  };

  const updateOnDividend = (timestamp, dividend, event) => {
    realizedPnL = realizedPnL.plus(dividend);
    PnL = realizedPnL.plus(unrealizedPnL);
    ROI = PnL.div(costOfInvestment);
    annualizedReturn = (t.length == 0) ? BN(0) : toAnnualReturn(ROI, t[0], timestamp);
    recordHistory(timestamp, event);
  };

  // const updateOnFee = (timestamp, fee) => {
  //   realizedPnL = realizedPnL.minus(fee);
  //   PnL = realizedPnL.plus(unrealizedPnL);
  //   ROI = PnL.div(costOfInvestment);
  //   annualizedReturn = (t.length == 0) ? BN(0) : toAnnualReturn(ROI, t[0], timestamp);
  //   recordHistory(timestamp, "Fee");
  // };

  // TODO: check
  const SECONDS_PER_YEAR = 31556926;
  // const MILISECONDS_PER_YEAR = 31556926000;
  const toAnnualReturn = (r, ti, tf) => {
    if (ti == tf) return BN(0);
    else return BN(r.plus(1).toNumber() ** (SECONDS_PER_YEAR / (tf - ti))).minus(1);
  };

  const recordHistory = (timestamp, event) => {
    t.push(timestamp);
    event_t.push(event);
    quantity_t.push(quantity);
    costOfInvestment_t.push(costOfInvestment);
    realizedPnL_t.push(realizedPnL);
    unrealizedPnL_t.push(unrealizedPnL);
    PnL_t.push(PnL);
    ROI_t.push(ROI);
    annualizedReturn_t.push(annualizedReturn);
  };

  const getEvents = async (filter, fromBlockNumber, toBlockNumber, chunkLimit) => {

    const chunks = [];
    const totalBlocks = toBlockNumber - fromBlockNumber;
    if (chunkLimit == 0 || totalBlocks <= chunkLimit) {
      chunks.push({ fromBlock: fromBlockNumber, toBlock: toBlockNumber });
    } else {
      const n = Math.ceil(totalBlocks / chunkLimit);
      let currentBlockNumber = fromBlockNumber;
      for (let i = 0; i < n; i++) {
        const fromRangeBlockNumber = currentBlockNumber;
        const toRangeBlockNumber = i == n - 1 ? toBlockNumber : currentBlockNumber + chunkLimit;
        chunks.push({ fromBlock: fromRangeBlockNumber, toBlock: toRangeBlockNumber });
        currentBlockNumber = toRangeBlockNumber + 1;
      }
    }

    const events = [];
    for (const chunk of chunks) {
      const chunkEvents = await contract.provider.getLogs({
        ...filter,
        fromBlock: chunk.fromBlock,
        toBlock: chunk.toBlock
      });
      if (chunkEvents.length > 0) events.push(...chunkEvents);
    }

    if (events.length == 0) return [];

    const parsedEvents = [];
    for (const event of events) {
      const parsedEvent = await contract.interface.parseLog(event);
      parsedEvent.blockNumber = event.blockNumber;
      parsedEvents.push(parsedEvent);
    }
    return parsedEvents;
  };

  const getTimestamps = (fromTimestamp, toTimestamp, freq) => {
    const timestamps = [];

    if (fromTimestamp == toTimestamp) {
      timestamps.push(fromTimestamp);
      return timestamps;
    }

    let currentTimestamp = toTimestamp;
    const deltaTimestamp = FREQ_TO_DELTA_TIMESTAMP[freq];
    while (currentTimestamp > fromTimestamp) {
      timestamps.push(currentTimestamp);
      currentTimestamp -= deltaTimestamp;
    }
    return timestamps;
  };

  const createEvents = async (eventName, fromTimestamp, toTimestamp, freq) => {
    const timestamps = getTimestamps(fromTimestamp, toTimestamp, freq);

    if (timestamps.length == 0) return [];

    const blocks = await getMassBlocks(network, timestamps);

    const events = [];
    for (const block of blocks) {
      events.push({
        name: eventName,
        blockNumber: block.number,
        timestamp: block.timestamp
      });
    }

    return events;
  };

  const compute = async (options) => {

    const { fromBlock = 0, toBlock = "latest", chunkLimit = 0, freq } = options;

    const toBlockNumber = toBlock === "latest" ? await position.provider.getBlockNumber() : toBlock;
    const fromBlockNumber = fromBlock;

    const depositFilter = await contract.filters.LiquidityAdded();
    const depositEvents = await getEvents(depositFilter, fromBlockNumber, toBlockNumber, chunkLimit);

    if (depositEvents.length == 0) return;

    const withdrawFilter = await contract.filters.LiquidityRemoved();
    const withdrawEvents = await getEvents(withdrawFilter, fromBlockNumber, toBlockNumber, chunkLimit);

    const currentTimestamp = getUnixTime(subMinutes(Date.now(), 10));
    const fromTimestamp = freq ? depositEvents[0].args.timestamp : currentTimestamp;
    const markToMarketEvents = await createEvents("MarkToMarket", fromTimestamp, currentTimestamp, freq);

    const events = [...depositEvents, ...withdrawEvents, ...markToMarketEvents];
    events.sort((e1, e2) => (e1.blockNumber > e2.blockNumber ? 1 : -1));

    const feePercentage = await contract.feePercentage();

    for (const event of events) {

      const pair = await getPair(
        network,
        pairSpotQuery,
        {
          id: position.pairAddress.toLowerCase(),
          blockNumber: event.blockNumber,
        },
      );

      const marketPrice = pair.reserveUSD / pair.totalSupply;

      if (event.name == "LiquidityAdded" || event.name == "LiquidityRemoved") {

        const { timestamp, amountA, amountB, lptAmount } = event.args;
        const nShares = toBN(lptAmount).scaleDown(18);

        const sharePriceUSD = marketPrice;

        const ev = {
          eventType: "",
          timestamp: timestamp,
          amountUSD: 0,
          amountA: amountA,
          amountB: amountB,
          lptAmount: lptAmount
        };

        if (event.name == "LiquidityAdded") {
          if (event.args.isDividend) {
            const nSharesScaled = nShares.times(100 - feePercentage).div(100);
            const dividend = nSharesScaled.times(sharePriceUSD);
            const fill = Fill(timestamp, nSharesScaled, sharePriceUSD);
            ev.eventType = "Dividend";
            ev.amountUSD = dividend;
            updateOnDividend(timestamp - 1, dividend, {...ev});
            ev.eventType = "Liquidity Added";
            updateOnFill(fill, {...ev});
          } else {
            const fill = Fill(timestamp, nShares, sharePriceUSD);
            ev.eventType = "Liquidity Added";
            ev.amountUSD = fill.quantity * fill.price;
            updateOnFill(fill, ev);
          }
        } else if (event.name == "LiquidityRemoved") {
          const fill = Fill(timestamp, nShares.times(-1), sharePriceUSD);
          ev.eventType = "Liquidity Removed";
          ev.amountUSD = -fill.quantity * fill.price;
          updateOnFill(fill, ev);
        }

      } else if (event.name == "MarkToMarket") {
        updateOnPrice(event.timestamp, marketPrice, { eventType: "MarkToMarket" });
      }

      blockNumber_t.push(event.blockNumber);
    }

    return;
  };

  const printPnL = (n = 3) => {
    const table = [];
    for (let i = 0; i < t.length; i++) {
      const row = {
        t: fromUnixTime(t[i]),
        blockNumber: blockNumber_t[i],
        Event: event_t[i].eventType,
        Quantity: quantity_t[i].toExponential(n),
        PnL: PnL_t[i].toExponential(n),
        RealizedPnL: realizedPnL_t[i].toExponential(n),
        UnrealizedPnL: unrealizedPnL_t[i].toExponential(n),
        CostOfInvestment: costOfInvestment_t[i].toExponential(n),
        ROI: ROI_t[i].toExponential(n),
        AnnualizedReturn: annualizedReturn_t[i].toExponential(n)
      };
      table.push(row);
    }
    console.table(table);
  };

  return {
    isShort,
    isLong,
    isNeutral,
    getLastQuantity,
    getLastCostOfInvestment,
    getLastRealizedPnL,
    getLastUnrealizedPnL,
    getLastPnL,
    getLastROI,
    getLastAnnualizedReturn,
    getLastMarketValue,
    t,
    blockNumber_t,
    event_t,
    quantity_t,
    costOfInvestment_t,
    realizedPnL_t,
    unrealizedPnL_t,
    PnL_t,
    ROI_t,
    annualizedReturn_t,
    fills,
    compute,
    printPnL
  };
};
